import { useCallback, useState } from 'react';
import { useToast } from '@chakra-ui/react';

const BLOCK_KEY = 'cnpj_ws_block_until';
const BLOCK_DURATION_MS = 2 * 60 * 1000;

const formatRemainingTime = (ms) => {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0 && seconds > 0) return `${minutes} min e ${seconds} s`;
  if (minutes > 0) return `${minutes} min`;
  return `${seconds} s`;
};

const getBlockUntil = () => {
  const stored = localStorage.getItem(BLOCK_KEY);
  return stored ? parseInt(stored, 10) : null;
};

const setBlockUntil = () => {
  const until = Date.now() + BLOCK_DURATION_MS;
  localStorage.setItem(BLOCK_KEY, String(until));
  return until;
};

const clearBlock = () => localStorage.removeItem(BLOCK_KEY);

// ─────────────────────────────────────────

const formatPhone = (ddd, phone) => {
  if (!ddd || !phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 9) return `(${ddd}) ${digits.slice(0, 5)}-${digits.slice(5)}`;
  if (digits.length === 8) return `(${ddd}) ${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `(${ddd}) ${phone}`;
};

const mapToFornecedor = (data) => {
  const est = data.estabelecimento ?? {};
  return {
    razaoSocial: data.razao_social ?? '',
    nomeFantasia: est.nome_fantasia ?? '',
    cep: est.cep ?? '',
    endereco: [est.tipo_logradouro, est.logradouro].filter(Boolean).join(' '),
    numero: est.numero ?? '',
    complemento: est.complemento ?? '',
    bairro: est.bairro ?? '',
    cidade: est.cidade?.nome ?? '',
    estado: est.estado?.sigla ?? '',
    pais: est.pais?.nome ?? '',
    telefone: formatPhone(est.ddd1, est.telefone1),
    celularWhatsApp: formatPhone(est.ddd2, est.telefone2) || formatPhone(est.ddd1, est.telefone1),
    isValidCNPJ: est?.situacao_cadastral === 'Ativa',
  };
};

// ─────────────────────────────────────────

export const useConsultaCNPJ = (onData) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const consultarCNPJ = useCallback(async (cnpj) => {
    const clean = cnpj.replace(/\D/g, '');
    if (clean.length !== 14) return;

    const blockUntil = getBlockUntil();
    if (blockUntil) {
      const remaining = blockUntil - Date.now();
      if (remaining > 0) {
        toast({
          title: 'Consulta temporariamente bloqueada.',
          description: `Aguarde ${formatRemainingTime(remaining)} antes de tentar novamente.`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      clearBlock();
    }

    setIsLoading(true);
    try {
      const res = await fetch(`https://publica.cnpj.ws/cnpj/${clean}`);
      //https://www.receitaws.com.br/v1/cnpj/ 

      if (res.status === 429) {
        const until = setBlockUntil();
        toast({
          title: 'Limite de consultas atingido.',
          description: `Novas consultas liberadas em ${formatRemainingTime(until - Date.now())}.`,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      if (res.status === 404) {
        toast({
          title: 'CNPJ não encontrado.',
          description: 'Verifique o número informado.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (!res.ok) return;

      const data = await res.json();
      toast({
        title: 'Dados do CNPJ carregados.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onData?.(mapToFornecedor(data));
    } catch {
      toast({
        title: 'Erro ao consultar CNPJ.',
        description: 'Não foi possível conectar ao serviço.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [onData, toast]);

  return { consultarCNPJ, isLoading };
};
